import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import io from 'socket.io-client';

import { isSessionExpiredError } from 'utils/authError';

axios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem("xsrfToken");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // ignore
  }
  return config;
});

const abortControllers = {};
let taskCheckRequestCount = 0;
let crmRequestCount = 0;
let currentSocket = null;
let currentRoom = {};

const cache = {};
const CACHE_DURATION = 5 * 60 * 1000;
const cacheableEndpoints = [
  'admin/searchMission',
  'admin/getTempWorkerPostulateMission',
  'admin/getMissionDetailsById',
  'admin/getTempWorkerInviteMission',
  // A referential held as a constant in the API: it cannot change while the
  // page is open, yet it was re-fetched on every company panel opening.
  'admin/getCollectiveInstallations',
];

const generateCacheKey = (url, data) => {
  const dataString = JSON.stringify(data || {});
  return `${url}|${dataString}`;
};

const updateCacheEntry = (url, data, newData) => {
  const cacheKey = generateCacheKey(url, data);
  cache[cacheKey] = {
    data: newData,
    timestamp: Date.now(),
  };
};

const isSameCachedMission = (mission, missionId) => {
  const id = missionId?.toString?.() || missionId;
  const realId = mission?.id_mission_real?.toString?.() || mission?.id_mission_real;
  const mongoId = mission?._id?.toString?.() || mission?._id;
  return Boolean(id && (realId === id || mongoId === id));
};

const patchSearchMissionCache = (missionId, patchMission) => {
  Object.keys(cache).forEach((key) => {
    if (!key.startsWith('admin/searchMission|')) return;

    const cacheEntry = cache[key];
    const cachedData = cacheEntry?.data;
    const cachedMissions = Array.isArray(cachedData)
      ? cachedData
      : cachedData?.missions;
    if (!Array.isArray(cachedMissions)) return;

    let hasPatchedMission = false;
    const patchedMissions = cachedMissions.map((mission) => {
      if (!isSameCachedMission(mission, missionId)) return mission;
      hasPatchedMission = true;
      return patchMission(mission);
    });

    if (!hasPatchedMission) return;

    cache[key] = {
      data: Array.isArray(cachedData)
        ? patchedMissions
        : { ...cachedData, missions: patchedMissions },
      timestamp: Date.now(),
    };
  });
};

// Cached reads scoped to one mission: its details and its candidate lists.
const MISSION_SCOPED_ENDPOINTS = [
  'admin/getTempWorkerPostulateMission',
  'admin/getTempWorkerInviteMission',
  'admin/getMissionDetailsById',
];

const readCachedPayload = (key) => {
  try {
    return JSON.parse(key.slice(key.indexOf('|') + 1));
  } catch {
    return null;
  }
};

/**
 * Drops every cached read of one mission, whatever search or page it was
 * cached under. Entries are keyed by the full payload ({ idMission, search }),
 * so updating a single key (as websocket events did with { idMission }) left
 * the entry the screen actually reads untouched.
 */
const invalidateMissionCache = (idMission) => {
  if (!idMission) return;
  const id = idMission.toString();
  Object.keys(cache).forEach((key) => {
    const endpoint = key.slice(0, key.indexOf('|'));
    if (!MISSION_SCOPED_ENDPOINTS.includes(endpoint)) return;
    if (readCachedPayload(key)?.idMission?.toString() === id) delete cache[key];
  });
};

/**
 * Drops every cached mission detail of one company. The detail carries the
 * company's post-it, and is keyed by the mission it was read for: a note saved
 * from any screen would otherwise stay stale in each of them.
 */
const invalidateCompanyMissionsCache = (idCompany) => {
  if (!idCompany) return;
  const id = idCompany.toString();
  Object.keys(cache).forEach((key) => {
    if (!key.startsWith('admin/getMissionDetailsById|')) return;
    // Populated object as served today; a bare id is matched all the same.
    const company = cache[key]?.data?.company;
    const cachedId = company?._id ?? company;
    if (cachedId?.toString?.() === id) delete cache[key];
  });
};

const isCacheValid = (cacheEntry) => {
  if (!cacheEntry) return false;
  return Date.now() - cacheEntry.timestamp < CACHE_DURATION;
};

const connectSocketWithToken = async () => {
  try {
    const token = localStorage.getItem('xsrfToken');
    if (token) {
      const decodedToken = jwtDecode(token);

      const newRoom = {
        userId: decodedToken.id || '',
      };

      // Vérifier si le socket actuel est connecté à la bonne salle
      if (
        currentSocket &&
        currentSocket?.connected &&
        currentRoom.userId === newRoom.userId
      ) {
        console.log('Socket déjà connecté à la bonne salle');
        return currentSocket;
      }

      // Si le socket existe mais n'est pas connecté à la bonne salle, le déconnecter
      if (currentSocket && !currentSocket?.connected) {
        console.log('Déconnexion du socket actuel');
        currentSocket.disconnect();
      }

      const socket = io(process.env.REACT_APP_API_URL_SOCKET, {
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
        transports: ['websocket'],
        // A function, not a value: each reconnection re-reads the token, which
        // the sliding session may have replaced since the first handshake.
        auth: (cb) => cb({ token: localStorage.getItem('xsrfToken') }),
      });

      // Attendre la connexion du socket
      await new Promise((resolve, reject) => {
        // Only the first handshake fails this way: once connected, the socket
        // is stored and must keep its own reconnection attempts.
        const failFirstHandshake = (err) => {
          // A socket never stored would keep retrying in the background,
          // invisible to every caller.
          socket.disconnect();
          reject(new Error(`Erreur de connexion : ${err.message}`));
        };

        socket.on('connect', async () => {
          socket.off('connect_error', failFirstHandshake);
          try {
            await socket.emit('joinRoom', newRoom);
            resolve(); // Résoudre la promesse une fois terminé
          } catch (error) {
            reject(error); // Rejeter en cas d'erreur
          }
        });

        // Gestion d'erreur si le socket échoue à se connecter
        socket.on('connect_error', failFirstHandshake);
      });

      currentSocket = socket;
      currentRoom = newRoom;
      return socket;
    }
    return null;
  } catch (error) {
    console.error('Erreur lors de la connexion Socket.IO :', error);
    throw error; // Relancer l'erreur si nécessaire
  }
};

const disconnectSocket = () => {
  if (currentSocket) {
    currentSocket.disconnect();
    currentSocket = null;
    currentRoom = {};
    console.log('Socket déconnecté');
  }
};

/** Avoid firing multiple modals when several API calls fail with invalid token. */
let sessionExpiredDispatched = false;

const dispatchSessionExpired = () => {
  if (sessionExpiredDispatched) return;
  sessionExpiredDispatched = true;
  disconnectSocket();
  window.dispatchEvent(new CustomEvent('session-expired'));
};

const renewTokenIfNeededAndConnectSocket = async () => {
  try {
    return await connectSocketWithToken();
  } catch (error) {
    console.error(
      'Erreur lors du renouvellement du token et de la connexion Socket.IO :',
      error
    );
  }
};

const getAbortController = (key) => {
  if (abortControllers[key]) {
    abortControllers[key].abort();
  }
  abortControllers[key] = new AbortController();
  return abortControllers[key];
};

/** Remove legacy body/query token; auth is Bearer via axios interceptor (except URL token for postRoleUser). */
const stripLegacyXsrfTokenFromPayload = (endpointPath, payload) => {
  if (endpointPath === 'user/role') return payload;
  if (payload == null || typeof payload !== 'object' || Array.isArray(payload))
    return payload;
  if (!Object.prototype.hasOwnProperty.call(payload, 'token')) return payload;
  const { token: _t, ...rest } = payload;
  return rest;
};

const readBlobAsText = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

/**
 * A request that asks for a Blob gets its error body as a Blob too. It is read
 * back as JSON in place, so that the caller, and the session check, find
 * `errorCode` where every other call has it. A body that is not JSON stays as
 * it came.
 */
const readBlobErrorBody = async (error) => {
  const body = error?.response?.data;
  if (typeof Blob === 'undefined' || !(body instanceof Blob)) return;
  try {
    error.response.data = JSON.parse(await readBlobAsText(body));
  } catch {
    // Not JSON: nothing more to read from it.
  }
};

const makeRequest = async (
  url,
  method,
  data,
  resolve,
  reject = () => {},
  options = {}
) => {
  const {
    skipCache = false,
    abortKey = url,
    keepMissionCache = false,
    responseType,
    timeoutMs,
  } = options;
  data = stripLegacyXsrfTokenFromPayload(url, data);

  try {
    const controller = getAbortController(abortKey);
    const isCacheable = cacheableEndpoints.some((endpoint) =>
      url.startsWith(endpoint)
    );
    const cacheKey = isCacheable ? generateCacheKey(url, data) : null;

    if (
      isCacheable &&
      cacheKey &&
      !skipCache &&
      cache[cacheKey] &&
      isCacheValid(cache[cacheKey])
    ) {
      resolve(cache[cacheKey].data);
      return;
    }

    const requestConfig = {
      method,
      url: `${process.env.REACT_APP_API_URL}${url}`,
      withCredentials: true,
      signal: controller.signal,
      ...(responseType && { responseType }),
    };
    if (timeoutMs) requestConfig.timeout = timeoutMs;

    if (method.toLowerCase() === 'get') {
      requestConfig.params = data;
    } else {
      requestConfig.data = data;
    }

    const response = await axios(requestConfig);

    if (response.data.errors) {
      reject(response.data.errors);
    } else {
      // Any successful write on a mission (refusal, pending contact,
      // assignment, invitation…) makes its cached lists stale: reopening the
      // mission within the cache window used to show the state before it.
      if (
        !isCacheable &&
        !keepMissionCache &&
        method.toLowerCase() !== 'get' &&
        data?.idMission
      ) {
        invalidateMissionCache(data.idMission);
      }
      if (isCacheable && cacheKey) {
        cache[cacheKey] = {
          data: response.data,
          timestamp: Date.now(),
        };
      }
      resolve(response.data);
    }
  } catch (error) {
    if (responseType === 'blob') await readBlobErrorBody(error);
    if (isSessionExpiredError(error)) {
      dispatchSessionExpired();
    }
    reject(error);
  }
};

// Each CRM call gets its own abort key: the default one, the url, would let a
// second call cancel the first (two ticks in a row, the panel and the page
// reading the same task). The key is dropped once answered, so the
// controllers do not pile up.
const makeCrmRequest = (url, payload, resolve, reject, options = {}) => {
  crmRequestCount += 1;
  const abortKey = `${url}#${crmRequestCount}`;
  const settle = (callback) => (value) => {
    delete abortControllers[abortKey];
    if (callback) callback(value);
  };
  return makeRequest(url, 'post', payload, settle(resolve), settle(reject), {
    abortKey,
    ...options,
  });
};

const cleanUpCache = () => {
  const now = Date.now();
  Object.keys(cache).forEach((key) => {
    if (now - cache[key].timestamp > CACHE_DURATION) {
      delete cache[key];
    }
  });
};

setInterval(cleanUpCache, 60 * 1000);

// Already logged inside; a refused handshake must not surface as an unhandled rejection.
connectSocketWithToken().catch(() => {});

const ApiDatabase = {
  getUserProfile: (payload, resolve, reject, options = {}) =>
    makeRequest('user/info', 'post', payload, resolve, reject, options),
  getTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest('tempWorker/info', 'post', payload, resolve, reject, options),
  postRoleUser: (payload, resolve, reject, options = {}) =>
    makeRequest('user/role', 'post', payload, resolve, reject, options),
  postPasswordRefresh: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'user/update/password',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  postEmailRefresh: (payload, resolve, reject, options = {}) =>
    makeRequest('user/update/email', 'put', payload, resolve, reject, options),
  postResendEmailToken: (payload, resolve, reject, options = {}) =>
    makeRequest('resend/tokenMail', 'post', payload, resolve, reject, options),
  postCreateTokenMail: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'resend/createToken',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getListLanguages: (resolve, reject, options = {}) =>
    makeRequest('language', 'get', {}, resolve, reject, options),
  postUserLanguage: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/create/language',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  deleteUserLanguage: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/delete/languages',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  getListTools: (resolve, reject, options = {}) =>
    makeRequest('tools', 'get', {}, resolve, reject, options),
  postUserTool: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/create/tools',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postUserToolDelete: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/delete/tools',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  deleteUserCV: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/delete/cv',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  getInfoMission: (payload, resolve, reject, options = {}) =>
    makeRequest(`mission/info`, 'get', payload, resolve, reject, options),
  postCandidate: (payload, resolve, reject, options = {}) =>
    makeRequest('mission/addUser', 'post', payload, resolve, reject, options),
  postVerifyUser: (payload, resolve, reject, options = {}) =>
    makeRequest('mission/verify', 'post', payload, resolve, reject, options),
  deleteUserMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'mission/deleteUser',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  postCreateExp: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/create/experience',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  putUpdateExp: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/update/experience',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  postDeleteExp: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/delete/experiences',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  getAllLicence: (resolve, reject, options = {}) =>
    makeRequest('licence', 'get', {}, resolve, reject, options),
  postDeleteLicence: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/delete/licence',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  postDeleteFormation: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/delete/training',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdateIban: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'tempWorker/update/bank',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdatePhone: (payload, resolve, reject, options = {}) =>
    makeRequest('user/update/phone', 'put', payload, resolve, reject, options),
  postUpdateAddress: (payload, resolve, reject, options = {}) =>
    makeRequest('address/add', 'post', payload, resolve, reject, options),
  postUpdateAddressTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/address/add', 'post', payload, resolve, reject, options),
  getAddress: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `address/${payload.id}`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheets: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/timeSheets/allInfo',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheetInfo: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `timeSheets/${payload.id}`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheetDay: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `timeSheets/getTimeSheetDay/${payload.id}`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheetHistory: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `timeSheets/${payload.id}/history`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdateDay: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'timeSheets/updateDayAdmin',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getAllUsersWithIdentityFilesToComplete: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      'admin/tempWorkersWithIdentityFilesToComplete',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTempWorkersValidatedByAI: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      'admin/tempWorkersValidatedByAI',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  // Licences and CACES to check, one row per temp worker: { page, limit, search }.
  getPendingLicences: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/licences/pending',
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  // Licences and CACES accepted by the AI, one row per entry: { page, limit, search }.
  getLicencesValidatedByAI: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/licences/validated-by-ai',
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  // Identity, current licences and licence history of one temp worker: { tempWorkerId }.
  getLicencesOfTempWorker: ({ tempWorkerId }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/licences/temp-worker/${encodeURIComponent(tempWorkerId)}`,
      'get',
      {},
      resolve,
      reject,
      options
    ),
  // { tempWorkerId, licenceId, licence, category, expiration: 'YYYY-MM-DD' | null }.
  postAcceptLicence: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/licences/accept',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  // { tempWorkerId, licenceId, reasonId }.
  postRefuseLicence: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/licences/refuse',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getUserWithIdentityFilesToComplete: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      'admin/tempWorkerWithIdentityFilesToComplete',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postRefuseTempWorkerFileAdmin: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/refuseTempWorkerFileAdmin',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postAcceptTempWorkerFileAdmin: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/acceptTempWorkerFileAdmin',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTempWorkerById: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `tempWorker/${payload.adminTempWorkerId}`,
      'get',
      {},
      resolve,
      reject,
      options
    ),
  getTempWorkerByIdUser: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `tempWorker/tempWorkerByIdUser/${payload.idUser}`,
      'get',
      {},
      resolve,
      reject,
      options
    ),
  getUserById: (payload, resolve, reject, options = {}) =>
    makeRequest(`user/${payload.id_user}`, 'get', {}, resolve, reject, options),
  postAcceptTempWorkerFileIdentityAdmin: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      'admin/acceptTempWorkerFileIdentityAdmin',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdateTempWorkerIdentityAdmin: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/tempWorker/identity/update',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postAcceptTempWorkerFileVisaAdmin: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/acceptTempWorkerFileVisaAdmin',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postAcceptTempWorkerVisaComplementaryDocument: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      'admin/acceptTempWorkerVisaComplementaryDocument',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postRefuseTempWorkerVisaComplementaryDocument: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      'admin/refuseTempWorkerVisaComplementaryDocument',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postAcceptTempWorkerFileVitalCardAdmin: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      'admin/acceptTempWorkerFileVitalCardAdmin',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postPresignFiles: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/presignFiles',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheetsBeSTT: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/getTimeSheetsBeSTT',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdateTimeSheet: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'timeSheets/updateCompany',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  updateTimeSheetStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'timeSheets/updateStatus',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanies: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getCompaniesList`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyInfo: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getCompanyInfo`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyMissionsAndTempWorkers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/companyMissionsAndTempWorkers`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getMinimumWage: (resolve, reject, options = {}) =>
    makeRequest('admin/getMinimumWage', 'get', {}, resolve, reject, options),
  postCoefficient: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/postCoefficient',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  searchMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/searchMission',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  globalSearch: (payload, resolve, reject) => {
    const { search, limit = 5 } = payload;
    makeRequest(
      'admin/globalSearch',
      'post',
      { search, limit },
      (data) => {
        resolve({
          missions: data?.missions || [],
          tempWorkers: data?.tempWorkers || [],
          companies: data?.companies || [],
          clientContacts: data?.clientContacts || [],
        });
      },
      reject
    );
  },
  getMissionDetailsById: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getMissionDetailsById`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getMissionDpae: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getMissionDpae`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  searchTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/searchTempWorker',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  assignTempWorkerToMission: (payload, resolve, reject, options = {}) =>
    makeRequest('mission/assign', 'post', payload, resolve, reject, options),
  requestSignContract: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/requestSignContract',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getMissionApplicationsViews: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/missionApplicationsViews', 'post', payload, resolve, reject, options),
  // Recording a visit changes nothing the mission's cached lists show.
  markMissionApplicationsSeen: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/missionApplicationsViews/seen', 'post', payload, resolve, reject, {
      abortKey: `admin/missionApplicationsViews/seen|${payload?.idMission}`,
      ...options,
      keepMissionCache: true,
    }),
  getTempWorkerPostulateMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/getTempWorkerPostulateMission',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  unassignMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/unassignMission',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  putDisplayCostEstimate: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/updateDisplayCostEstimate',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getDisplayCostEstimate: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getDisplayCostEstimate`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getScheduleByIdMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getScheduleByIdMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  linkCompanyToBigAccount: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/linkCompanyToBigAccount',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getBigAccounts: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/getBigAccounts',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  addBigAccount: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/addBigAccount',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyBigAccount: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getCompanyBigAccount`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  updScheduleByIdMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'mission/updScheduleByIdMission',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  deleteCompanyBigAccount: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/deleteCompanyBigAccount',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  deleteBigAccount: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/deleteBigAccount',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  requestGenerationContract: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/requestGenerationContract',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  updateContractFromMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/updateContractFromMission',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  regenerateAmendment: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/regenerateAmendment',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postAddCompany: (payload, resolve, reject, options = {}) =>
    makeRequest('company/add', 'post', payload, resolve, reject, options),
  getReasonRefusesDocuments: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/reasonRefusesDocuments`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getRatingTreatedTempWorkers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/ratingTreatedTempWorkers`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getRatingUntreatedTempWorkers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/ratingUntreatedTempWorkers`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getRatingTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/ratingTempWorker`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getRatingTempWorkerByMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/ratingTempWorkerByMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postTreatmentRatingTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/treatmentRatingTempWorker',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getNbRatingUntreatedTempWorkers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/nbRatingUntreatedTempWorkers`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getReviewsByTempWorkerGroupByMissions: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/reviewsByTempWorkerGroupByMissions`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getReviewsByTempworkerByMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/reviewsByTempworkerByMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getTempWorkerInviteMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getTempWorkerInviteMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getNbReviewsTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/nbReviewsTempWorker`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getNationalities: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/nationalities`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getAllQualifications: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `qualification/getAllQualifications`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postQualification: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'qualification/postQualification',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  updateQualification: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'qualification/putQualification',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getQualificationByID: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `qualification/getQualificationByID`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  delQualification: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'qualification/delQualification',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  activateQualification: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'qualification/activateQualification',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  // AI-assisted qualification creation: nothing below writes a qualification.
  getQualificationAiSuggestStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/qualifications/ai-suggest/status',
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postQualificationAiSuggestion: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/qualifications/ai-suggest',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getQualificationAiSuggestion: (
    { suggestionId, ...payload },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/qualifications/ai-suggest/${encodeURIComponent(suggestionId)}`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getReasonExcludeAgency: (payload, resolve, reject, options = {}) =>
    makeRequest('vivier/reasonExcludeAgency', 'get', payload, resolve, reject, options),
  postExcludeAgency: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'vivier/postExcludeAgency',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getLocalisationPreferenceUser: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/localisationPreference`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  updLocalisationPreferenceUsers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/localisationPreferenceUsers',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  deleteVivier: (payload, resolve, reject, options = {}) =>
    makeRequest('vivier/delete', 'post', payload, resolve, reject, options),
  deleteVivierUser: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'vivier/deleteCandidateVivier',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  updUserVivierPreferenceStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'vivier/updUserVivierPreferenceStatus',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getCommentsByTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/commentsByTempWorker`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getNbCommentsByTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/nbCommentsByTempWorker`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postCommentTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/commentTempWorker',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  deleteCommentTempWorker: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/commentTempWorker',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  getVivierById: (payload, resolve, reject, options = {}) =>
    makeRequest(`vivier/vivierById`, 'get', payload, resolve, reject, options),
  updateUsersVivierStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'vivier/updateUsersVivierStatus',
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  getUserVivierArchiveReason: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/userVivierArchiveReason`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getUserViviers: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/userViviers`, 'get', payload, resolve, reject, options),
  getUserMissions: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/userMissions`, 'get', payload, resolve, reject, options),
  getUserRefusedMissions: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/userRefusedMissions`, 'get', payload, resolve, reject, options),
  getUserUnassignedMissions: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/userUnassignedMissions`, 'get', payload, resolve, reject, options),
  getUserCandidatures: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/userCandidatures`, 'get', payload, resolve, reject, options),
  getReasonRefusesMission: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/reasonRefusesMission`, 'get', payload, resolve, reject, options),
  getReasonUnassignMission: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/reasonUnassignMission`, 'get', payload, resolve, reject, options),
  getCollectiveAgreement: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/collectiveAgreement`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  deleteAdminVivierFavorite: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/adminVivierFavorite',
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  searchCompanyInfosBySiret: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/searchCompanyInfosBySiret`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postAdminVivierFavorite: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/adminVivierFavorite',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  searchCompanyInfosByName: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/searchCompanyInfosByName`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheetHoursVerified: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/getTimeSheetHoursVerified',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getVivierListUsers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `vivier/vivierListUser`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getVivierAllPage: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `vivier/vivierAllPage`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getVivierFiche: (payload, resolve, reject, options = {}) =>
    makeRequest(`vivier/vivierFiche`, 'get', payload, resolve, reject, options),
  getMissionVivier: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `vivier/missionVivier`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getLogVivier: (payload, resolve, reject, options = {}) =>
    makeRequest(`vivier/logVivier`, 'get', payload, resolve, reject, options),
  getVivierAll: (payload, resolve, reject, options = {}) =>
    makeRequest(`vivier/vivierAll`, 'get', payload, resolve, reject, options),
  postCancelMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/cancelMission`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postDeleteMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/deleteMission`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getInfoMissionCancelDelete: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `mission/infoMissionCancelDelete`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  updateMissionStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/updateMissionStatus`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  missionCancellation: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/missionCancellation', 'post', payload, resolve, reject, options),
  searchMissionCompanies: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/searchMissionCompanies`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  searchMissionAssignedTempWorkers: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/searchMissionAssignedTempWorkers`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getReasonsMissionSuspension: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getReasonsMissionSuspension`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getDepartmentMissions: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/departmentMissions`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanySuppliers: (payload, resolve, reject, options = {}) =>
    makeRequest('companySupplier', 'get', payload, resolve, reject, options),
  getCityDepartments: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `city/departments`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getCitiesByDepartments: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `city/citiesByDepartments`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postTempWorkerAssignationRefused: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/tempWorkerAssignationRefused`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postTempWorkerPendingContact: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/tempWorkerPendingContact`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  putTempWorkerAssignationRefused: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/tempWorkerAssignationRefused`,
      'put',
      payload,
      resolve,
      reject,
      options
    ),
  postDeleteContractHistory: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/deleteContractHistory`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getQualificationsActive: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `qualification/getQualificationsActive`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getTypesQualifications: (payload, resolve, reject) => {
    axios({
      method: 'get',
      url: `${process.env.REACT_APP_API_URL}qualification/getTypesQualifications`,
      withCredentials: true,
    })
      .then((res) => resolve(res.data))
      .catch((err) => reject(err));
  },
  postMissionDetail: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postMissionDetail`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postDuplicateMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/duplicateMission`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postViviersMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postViviersMission`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postViviersMissionNotification: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postViviersMissionNotification`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postViviersMissionNotificationGlobal: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/postViviersMissionNotificationGlobal`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getVivierNotificationMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getVivierNotificationMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getLastMissionNotificationGlobal: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getLastMissionNotificationGlobal`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getDescriptionCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getDescriptionCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postDescriptionCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postDescriptionCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdateCompanyOverview: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postUpdateCompanyOverview`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  // Billing address and payment term of a company (JBWEB-1268).
  getCompanyBillingInformation: ({ idCompany }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/billingInformation`,
      'get',
      {},
      resolve,
      reject,
      options
    ),
  // The company's invoices of one year (JBWEB-1308). One history at a time: a
  // newer read, another year or company, cancels the one before it.
  getCompanyInvoices: ({ idCompany, year }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/invoices`,
      'get',
      { year },
      resolve,
      reject,
      {
        abortKey: 'admin/companies/:idCompany/invoices',
        ...options,
      }
    ),
  // The company's account statement (customer account 411 read at Pennylane),
  // as a PDF Blob. Days are `YYYY-MM-DD`. An error body is read back as JSON
  // by `readBlobErrorBody`, so `errorCode` sits where every other call has it.
  getCompanyAccountStatement: ({ idCompany, from, to }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/accountStatement`,
      'get',
      { from, to },
      resolve,
      reject,
      {
        ...options,
        responseType: 'blob',
      }
    ),
  // The billing contacts the statement may be mailed to, as the invoice mail
  // offers them: `{ data: { contacts } }`.
  getCompanyAccountStatementRecipients: ({ idCompany }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/accountStatement/recipients`,
      'get',
      {},
      resolve,
      reject,
      {
        abortKey: 'admin/companies/:idCompany/accountStatement/recipients',
        ...options,
      }
    ),
  // The statement of the period mailed in PDF: recipients by reference only,
  // never an address. Its own abort key: no reading ever cancels a send.
  postCompanyAccountStatementMail: ({ idCompany, from, to, recipients }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/accountStatement/mail`,
      'post',
      { from, to, recipients },
      resolve,
      reject,
      {
        abortKey: 'admin/companies/:idCompany/accountStatement/mail',
        ...options,
      }
    ),
  putCompanyBillingInformation: ({ idCompany, ...billingInformation }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/billingInformation`,
      'put',
      billingInformation,
      resolve,
      reject,
      options
    ),
  // Who receives the invoices and the reminders of a company (JBWEB-1285):
  // its members, with their two notifications, and the contacts without an
  // account. One company at a time: a newer read cancels the one before it.
  getCompanyBillingContacts: ({ idCompany }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/billingContacts`,
      'get',
      {},
      resolve,
      reject,
      {
        abortKey: 'admin/companies/:idCompany/billingContacts',
        ...options,
      }
    ),
  // contact: { email, firstname, lastname, phone?, invoices, reminders }.
  postCompanyBillingContact: ({ idCompany, ...contact }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/billingContacts`,
      'post',
      contact,
      resolve,
      reject,
      options
    ),
  // changes: only the fields the admin changed; `phone: null` clears it.
  putCompanyBillingContact: (
    { idCompany, contactId, ...changes },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/billingContacts/${encodeURIComponent(contactId)}`,
      'put',
      changes,
      resolve,
      reject,
      options
    ),
  deleteCompanyBillingContact: ({ idCompany, contactId }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/billingContacts/${encodeURIComponent(contactId)}`,
      'delete',
      {},
      resolve,
      reject,
      options
    ),
  postAddCompanyMember: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postAddCompanyMember`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyEmployee: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getCompanyEmployee`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  // The active and the archived lists share the url: each status gets its own
  // abort key, or the second request would cancel the first.
  getAdminCompanyPerimeters: (
    { idCompany, status },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/perimeters`,
      'get',
      { status },
      resolve,
      reject,
      {
        abortKey: `admin/companies/${idCompany}/perimeters?status=${status}`,
        ...options,
      }
    ),
  postAdminCompanyPerimeter: (
    { idCompany, name, memberIds },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/perimeters`,
      'post',
      { name, memberIds },
      resolve,
      reject,
      options
    ),
  // `changes` holds only what changed: name and/or the full memberIds list.
  patchAdminCompanyPerimeter: (
    { idCompany, id, ...changes },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/perimeters/${encodeURIComponent(id)}`,
      'patch',
      changes,
      resolve,
      reject,
      options
    ),
  // transferTo: the id of another active perimeter, or 'none'.
  getAdminCompanyPerimeterUsage: (
    { idCompany, id, transferTo },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/perimeters/${encodeURIComponent(id)}/usage`,
      'get',
      { transferTo },
      resolve,
      reject,
      options
    ),
  // transferTo: the id of another active perimeter, or null for none. Always
  // sent: the api refuses an archive without it.
  postArchiveAdminCompanyPerimeter: (
    { idCompany, id, transferTo },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/perimeters/${encodeURIComponent(id)}/archive`,
      'post',
      { transferTo },
      resolve,
      reject,
      options
    ),
  deleteAdminCompanyPerimeter: ({ idCompany, id }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/perimeters/${encodeURIComponent(id)}`,
      'delete',
      {},
      resolve,
      reject,
      options
    ),
  postRestoreAdminCompanyPerimeter: (
    { idCompany, id },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/perimeters/${encodeURIComponent(id)}/restore`,
      'post',
      {},
      resolve,
      reject,
      options
    ),
  // perimeterIds: the member's full list of active perimeters, not a change.
  setAdminMemberPerimeters: (
    { idCompany, idEmployee, perimeterIds },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/members/${encodeURIComponent(idEmployee)}/perimeters`,
      'put',
      { perimeterIds },
      resolve,
      reject,
      options
    ),
  // The read has its own abort key: the save goes to the same url and must
  // not cancel it, nor be cancelled by a retry of the read.
  getAdminMemberNotifications: (
    { idCompany, idEmployee },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/members/${encodeURIComponent(idEmployee)}/notifications`,
      'get',
      {},
      resolve,
      reject,
      {
        abortKey: `admin/companies/${idCompany}/members/${idEmployee}/notifications|read`,
        ...options,
      }
    ),
  // levels: only the changed subjects, e.g. { contracts: 'perimeter' }.
  setAdminMemberNotifications: (
    { idCompany, idEmployee, levels },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/companies/${encodeURIComponent(idCompany)}/members/${encodeURIComponent(idEmployee)}/notifications`,
      'put',
      levels,
      resolve,
      reject,
      options
    ),
  postEditCompanyMember: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postEditCompanyMember`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postDelCompanyMember: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postDelCompanyMember`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postSendMailLinkCompanyEmployee: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postSendMailLinkCompanyEmployee`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postNightHoursCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postNightHoursCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getNightHoursCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getNightHoursCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getOvertimesCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getOvertimesCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postOvertimesCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postOvertimesCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getSundayHoursCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getSundayHoursCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postSundayHoursCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postSundayHoursCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getHolidayHoursCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getHolidayHoursCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postHolidayHoursCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postHolidayHoursCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getHolidays: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/getHolidays', 'get', payload, resolve, reject, options),
  getCollectiveInstallations: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/getCollectiveInstallations', 'get', payload, resolve, reject, options),
  getCollectiveInstallationsCompany: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/collectiveInstallationsCompany', 'get', payload, resolve, reject, options),
  postCollectiveInstallationsCompany: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/collectiveInstallationsCompany', 'post', payload, resolve, reject, options),
  getRestaurantTicketCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getRestaurantTicketCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getRestaurantTicketModalityListCompany: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/getRestaurantTicketModalityListCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postRestaurantTicketCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postRestaurantTicketCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getPostItCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getPostItCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postPostItCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postPostItCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getBonusListCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getBonusListCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getBonusUnitListCompany: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getBonusUnitListCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postAddBonusesAndAllowances: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postAddBonusesAndAllowances`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postUpdBonusesAndAllowances: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postUpdBonusesAndAllowances`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getBonusAndAllowance: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getBonusAndAllowance`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postDelBonusesAndAllowances: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postDelBonusesAndAllowances`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getBonusesAndAllowancesOtherCompany: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/getBonusesAndAllowancesOtherCompany`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postBonusesAndAllowancesOtherCompany: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/postBonusesAndAllowancesOtherCompany`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getEmployeesToAssignMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/getEmployeesToAssignMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postMissionAdminEmployeeAssigned: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postMissionAdminEmployeeAssigned`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  postImportedTimeSheet: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/postImportedTimeSheet`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  // The « facteurs de pénibilité » referential, readable by company and admin.
  getRiskyPositions: (payload, resolve, reject, options = {}) =>
    makeRequest(`riskyPositions`, 'get', payload, resolve, reject, options),
  // Job descriptions of any company, served by /api/admin/missionTemplates.
  getAdminMissionTemplates: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/missionTemplates`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getAdminMissionTemplate: ({ id, ...payload }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/missionTemplates/${id}`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postAdminMissionTemplate: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/missionTemplates`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  patchAdminMissionTemplate: (
    { id, ...payload },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/missionTemplates/${id}`,
      'patch',
      payload,
      resolve,
      reject,
      options
    ),
  postDuplicateAdminMissionTemplate: (
    { id, ...payload },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/missionTemplates/${id}/duplicate`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  deleteAdminMissionTemplate: (
    { id, ...payload },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/missionTemplates/${id}`,
      'delete',
      payload,
      resolve,
      reject,
      options
    ),
  // Each check gets its own abort key: the default one, the url, would cancel
  // the previous check when the admin adds tasks in a row.
  postAdminCheckMissionTemplateTask: (payload, resolve, reject, options = {}) => {
    taskCheckRequestCount += 1;
    return makeRequest(
      `admin/missionTemplates/tasks/check`,
      'post',
      payload,
      resolve,
      reject,
      {
        abortKey: `admin/missionTemplates/tasks/check#${taskCheckRequestCount}`,
        timeoutMs: 20000,
        ...options,
      }
    );
  },
  getCompaniesForMissionCreation: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/creationMission/getCompaniesForMissionCreation`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyQualificationSuggestionsFromRecentMissions: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/creationMission/getCompanyQualificationSuggestionsFromRecentMissions`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getRecentContactNamesForMissionCreation: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/creationMission/getRecentContactNamesForMissionCreation`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getListEmployeesForMissionCreation: (
    payload,
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/creationMission/getListEmployeesForMissionCreation`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  createMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/creationMission/createMission`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  // The coefficient the company grid gives for a wage, before the mission
  // exists ({ data: { coefficient } }, null = no usable range). One abort key:
  // another company or wage cancels the former read. The token is sent by the
  // interceptor.
  getCompanyCoefficient: (
    { idCompany, wagePerHour },
    resolve,
    reject,
    options = {}
  ) =>
    makeRequest(
      `admin/getCompanyCoefficient`,
      'get',
      { idCompany, wagePerHour },
      resolve,
      reject,
      { abortKey: 'admin/getCompanyCoefficient', ...options }
    ),
  inviteUsersToMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/creationMission/inviteUsersToMission`,
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTempWorkersRatingByMission: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/creationMission/getTempWorkersRatingByMission`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  listPayslips: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/listPayslips', 'get', payload, resolve, reject, options),
  listPayslipsByWorker: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/listPayslipsByWorker', 'get', payload, resolve, reject, options),
  importPayslips: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/importPayslips',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getTimeSheetAnalytics: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/timeSheetAnalytics',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  deletePayslips: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/deletePayslips',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getPayslipImportStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(
      `admin/payslipImportStatus/${payload.id}`,
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getPayslipImportHistory: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/payslipImportHistory',
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  exportPayslipsCSV: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/exportPayslipsCSV',
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  payslipStats: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/payslipStats', 'get', payload, resolve, reject, options),
  listInvoices: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/listInvoices', 'get', payload, resolve, reject, options),
  listInvoicesByCompany: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/listInvoicesByCompany', 'get', payload, resolve, reject, options),
  // Invoice import, BeSTT then Pennylane (JBWEB-1266). `invoiceNumbers`
  // narrows it to those invoices of the month (JBWEB-1274): without it, the
  // body stays the one of an import of the month.
  postInvoiceImport: ({ period, invoiceNumbers }, resolve, reject, options = {}) =>
    makeRequest(
      'admin/invoices/imports',
      'post',
      invoiceNumbers ? { period, invoiceNumbers } : { period },
      resolve,
      reject,
      options,
    ),
  getInvoiceImportRun: ({ runId }, resolve, reject, options = {}) =>
    makeRequest(`admin/invoices/imports/${encodeURIComponent(runId)}`, 'get', {}, resolve, reject, options),
  getInvoiceImportState: ({ period }, resolve, reject, options = {}) =>
    makeRequest('admin/invoices/imports/state', 'get', { period }, resolve, reject, options),
  // Retry the month's invoices in error, or some of them (JBWEB-1267).
  postInvoiceImportRetry: ({ period, invoiceNumbers }, resolve, reject, options = {}) =>
    makeRequest(
      'admin/invoices/imports/retry',
      'post',
      invoiceNumbers ? { period, invoiceNumbers } : { period },
      resolve,
      reject,
      options,
    ),
  // The month's invoices still in error, grouped by error code, for the
  // "À corriger" tab of the import journal (JBWEB-1280).
  getInvoiceImportErrors: ({ period }, resolve, reject, options = {}) =>
    makeRequest('admin/invoices/imports/errors', 'get', { period }, resolve, reject, options),
  // The month's import runs, newest first, a page at a time (JBWEB-1281).
  // Same address as the launch of an import (POST): its own abort key, so that
  // reading the history and launching an import never cancel each other.
  getInvoiceImportRuns: ({ period, page, limit }, resolve, reject, options = {}) =>
    makeRequest('admin/invoices/imports', 'get', { period, page, limit }, resolve, reject, {
      abortKey: 'admin/invoices/imports?list',
      ...options,
    }),
  // The invoices of a month, one page at a time (JBWEB-1269), narrowed by a
  // search: the text `q` and where it is looked for, `scope` (JBWEB-1270), and
  // by `status` (JBWEB-1271). Not cached, and a newer read cancels the one
  // before it (same abort key): the answer to the last filters asked is the one
  // that stays.
  // `groupBy` ("company" or "group") asks for the rows of the grouped list
  // instead of the invoices; `companyId` ("none" for no company) or `groupId`
  // narrows to the invoices of one row (JBWEB-1309). A row reads with its own
  // `options.abortKey`, so that it never cancels the list, nor another row.
  getMonthInvoices: (
    { period, page, limit, q, scope, status, groupBy, companyId, groupId },
    resolve,
    reject,
    options = {},
  ) =>
    makeRequest(
      'admin/invoices',
      'get',
      { period, page, limit, q, scope, status, group_by: groupBy, companyId, groupId },
      resolve,
      reject,
      options,
    ),

  // The CSV of the invoices of a month picked in the list (JBWEB-1273), as a
  // Blob: decoded as text, the file would lose its BOM, and Excel its accents.
  // The file name is not read from the answer: CORS does not expose
  // Content-Disposition.
  postInvoiceExport: ({ period, numbers }, resolve, reject, options = {}) =>
    makeRequest('admin/invoices/export', 'post', { period, numbers }, resolve, reject, {
      ...options,
      responseType: 'blob',
    }),
  // The invoice of the detail panel (JBWEB-1274), by its BeSTT number.
  // One panel at a time: a newer read cancels the one before it.
  getInvoiceDetail: ({ number }, resolve, reject, options = {}) =>
    makeRequest(`admin/invoices/${encodeURIComponent(number)}`, 'get', {}, resolve, reject, {
      abortKey: 'admin/invoices/:number',
      ...options,
    }),
  // Its PDF, as a Blob for the preview; its own abort key, so that reading the
  // invoice and its PDF never cancel each other.
  getInvoicePdf: ({ number }, resolve, reject, options = {}) =>
    makeRequest(`admin/invoices/${encodeURIComponent(number)}/pdf`, 'get', {}, resolve, reject, {
      abortKey: 'admin/invoices/:number/pdf',
      ...options,
      responseType: 'blob',
    }),
  // Its history (JBWEB-1278), newest first; its own abort key too.
  getInvoiceEvents: ({ number }, resolve, reject, options = {}) =>
    makeRequest(`admin/invoices/${encodeURIComponent(number)}/events`, 'get', {}, resolve, reject, {
      abortKey: 'admin/invoices/:number/events',
      ...options,
    }),
  // Its category and payment term (JBWEB-1275, JBWEB-1276): only the fields
  // the admin changed, `{ categoryId?, paymentTerm? }`.
  putInvoiceInformation: ({ number, ...information }, resolve, reject, options = {}) =>
    makeRequest(`admin/invoices/${encodeURIComponent(number)}/information`, 'put', information, resolve, reject, {
      abortKey: 'admin/invoices/:number/information',
      ...options,
    }),
  // Its appendices (JBWEB-1277), oldest first.
  getInvoiceAppendices: ({ number }, resolve, reject, options = {}) =>
    makeRequest(`admin/invoices/${encodeURIComponent(number)}/appendices`, 'get', {}, resolve, reject, {
      abortKey: 'admin/invoices/:number/appendices',
      ...options,
    }),
  // One file, alone in the multipart field `file`: the api refuses any other
  // part. The browser sets the multipart boundary itself.
  postInvoiceAppendix: ({ number, file }, resolve, reject, options = {}) => {
    const body = new FormData();
    body.append('file', file);
    return makeRequest(`admin/invoices/${encodeURIComponent(number)}/appendices`, 'post', body, resolve, reject, {
      abortKey: 'admin/invoices/:number/appendices|add',
      ...options,
    });
  },
  // The file of one appendix, as a Blob. One abort key per appendix: under a
  // shared key, downloading one would cancel the download of another.
  getInvoiceAppendixFile: ({ number, appendixId }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/invoices/${encodeURIComponent(number)}/appendices/${encodeURIComponent(appendixId)}/file`,
      'get',
      {},
      resolve,
      reject,
      {
        abortKey: `admin/invoices/:number/appendices/${appendixId}/file`,
        ...options,
        responseType: 'blob',
      },
    ),
  // The file of an appendix deposited directly in Pennylane (JBWEB-1346), read
  // there by the api, as a Blob. One abort key per appendix, for the same reason.
  getInvoicePennylaneAppendixFile: ({ number, pennylaneAppendixId }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/invoices/${encodeURIComponent(number)}/appendices/pennylane/${encodeURIComponent(pennylaneAppendixId)}/file`,
      'get',
      {},
      resolve,
      reject,
      {
        abortKey: `admin/invoices/:number/appendices/pennylane/${pennylaneAppendixId}/file`,
        ...options,
        responseType: 'blob',
      },
    ),
  deleteInvoiceAppendix: ({ number, appendixId }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/invoices/${encodeURIComponent(number)}/appendices/${encodeURIComponent(appendixId)}`,
      'delete',
      undefined,
      resolve,
      reject,
      { abortKey: `admin/invoices/:number/appendices/${appendixId}|delete`, ...options },
    ),
  // An admin found an `unconfirmed` appendix in Pennylane: Jobbiz records it sent.
  postInvoiceAppendixConfirm: ({ number, appendixId }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/invoices/${encodeURIComponent(number)}/appendices/${encodeURIComponent(appendixId)}/confirm`,
      'post',
      undefined,
      resolve,
      reject,
      { abortKey: `admin/invoices/:number/appendices/${appendixId}|confirm`, ...options },
    ),
  // Its internal comments (JBWEB-1279), oldest first: Jobbiz only, never Pennylane.
  getInvoiceComments: ({ number }, resolve, reject, options = {}) =>
    makeRequest(`admin/invoices/${encodeURIComponent(number)}/comments`, 'get', {}, resolve, reject, {
      abortKey: 'admin/invoices/:number/comments',
      ...options,
    }),
  // `{ text }`, written by the connected admin. Its own abort key: posting
  // never cancels the read of the list, nor the other way round.
  postInvoiceComment: ({ number, text }, resolve, reject, options = {}) =>
    makeRequest(`admin/invoices/${encodeURIComponent(number)}/comments`, 'post', { text }, resolve, reject, {
      abortKey: 'admin/invoices/:number/comments|add',
      ...options,
    }),
  // `{ text }`, by its author only. One abort key per comment: editing one
  // never cancels the edit of another, nor the read.
  patchInvoiceComment: ({ number, id, text }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/invoices/${encodeURIComponent(number)}/comments/${encodeURIComponent(id)}`,
      'patch',
      { text },
      resolve,
      reject,
      { abortKey: `admin/invoices/:number/comments/${id}|edit`, ...options },
    ),
  // Its author or a super-admin only (JBWEB-1389). One abort key per comment:
  // deleting one never cancels the deletion of another, nor the read.
  deleteInvoiceComment: ({ number, id }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/invoices/${encodeURIComponent(number)}/comments/${encodeURIComponent(id)}`,
      'delete',
      undefined,
      resolve,
      reject,
      { abortKey: `admin/invoices/:number/comments/${id}|delete`, ...options },
    ),
  // What the mail modal shows (JBWEB-1282) for the numbers selected and a
  // scope. A scope chosen anew cancels the reading of the one before it.
  postInvoiceMailPrepare: ({ invoiceNumbers, scope }, resolve, reject, options = {}) =>
    makeRequest('admin/invoices/mail/prepare', 'post', { invoiceNumbers, scope }, resolve, reject, {
      abortKey: 'admin/invoices/mail/prepare',
      ...options,
    }),
  // The send: recipients by reference only, never an address. Its own abort
  // key, so that no reading of the modal ever cancels a send under way.
  postInvoiceMail: ({ requestId, scope, invoiceNumbers, companies }, resolve, reject, options = {}) =>
    makeRequest('admin/invoices/mail', 'post', { requestId, scope, invoiceNumbers, companies }, resolve, reject, {
      abortKey: 'admin/invoices/mail|send',
      ...options,
    }),
  // One tab of the reminders of unpaid invoices (JBWEB-1283), a page of it,
  // narrowed by the search of the invoice list (`q`, `scope`). A newer read
  // cancels the one before it: the answer to the tab and search asked last
  // stays.
  getInvoiceReminders: ({ tab, q, scope, page, limit }, resolve, reject, options = {}) =>
    makeRequest('admin/invoices/reminders', 'get', { tab, q, scope, page, limit }, resolve, reject, options),
  // The counter of the tab and the cards of its screen: `{ toDo, lateAmount, litigation }`.
  getInvoiceRemindersSummary: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/invoices/reminders/summary', 'get', {}, resolve, reject, options),
  // Stops or restarts the automatic reminders of one invoice. One abort key per
  // invoice: changing one never cancels the change of another.
  putInvoiceReminderSuspension: ({ number, suspended }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/invoices/${encodeURIComponent(number)}/reminders/suspension`,
      'put',
      { suspended },
      resolve,
      reject,
      { abortKey: `admin/invoices/:number/reminders/suspension|${number}`, ...options },
    ),
  // "Tout relancer": 202 `{ runId }`. Without a list, the api takes every
  // invoice of "À faire"; it refuses any other key in the body.
  postInvoiceReminderRun: ({ invoiceNumbers } = {}, resolve, reject, options = {}) =>
    makeRequest(
      'admin/invoices/reminders/runs',
      'post',
      invoiceNumbers ? { invoiceNumbers } : {},
      resolve,
      reject,
      options,
    ),
  // Where a run stands: `{ run: { status, counters, … } }`.
  getInvoiceReminderRun: ({ runId }, resolve, reject, options = {}) =>
    makeRequest(`admin/invoices/reminders/runs/${encodeURIComponent(runId)}`, 'get', {}, resolve, reject, {
      abortKey: 'admin/invoices/reminders/runs/:runId',
      ...options,
    }),
  // What the manual reminder modal shows for one invoice: the step asked, or
  // the api's own choice without `level`. A template chosen anew cancels the
  // reading of the one before it.
  getInvoiceReminderDraft: ({ number, level }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/invoices/${encodeURIComponent(number)}/reminders/draft`,
      'get',
      level ? { level } : {},
      resolve,
      reject,
      { abortKey: 'admin/invoices/:number/reminders/draft', ...options },
    ),
  // A reminder sent by hand: recipients by reference only, never an address.
  // Its own abort key, so that no reading of the modal ever cancels a send.
  postInvoiceManualReminder: (
    { number, requestId, level, message, recipients, suspendAutomatic },
    resolve,
    reject,
    options = {},
  ) =>
    makeRequest(
      `admin/invoices/${encodeURIComponent(number)}/reminders`,
      'post',
      { requestId, level, message, recipients, suspendAutomatic },
      resolve,
      reject,
      { abortKey: 'admin/invoices/:number/reminders|send', ...options },
    ),
  // Hands a late invoice over to litigation (JBWEB-1284): no body, 200
  // `{ litigation: { since, by } }`. One abort key per invoice and gesture, so
  // that no other request ever cancels it.
  postInvoiceLitigation: ({ number }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/invoices/${encodeURIComponent(number)}/litigation`,
      'post',
      {},
      resolve,
      reject,
      { abortKey: `admin/invoices/:number/litigation|start|${number}`, ...options },
    ),
  // Takes it out again: 200 `{ litigation: null, nextAutomaticAt }`.
  deleteInvoiceLitigation: ({ number }, resolve, reject, options = {}) =>
    makeRequest(
      `admin/invoices/${encodeURIComponent(number)}/litigation`,
      'delete',
      {},
      resolve,
      reject,
      { abortKey: `admin/invoices/:number/litigation|end|${number}`, ...options },
    ),
  // Cancels the request still running under an abort key, e.g. when the panel
  // that asked for it closes.
  abortRequest: (abortKey) => {
    abortControllers[abortKey]?.abort();
  },
  getInvoiceImportStatus: (payload, resolve, reject, options = {}) =>
    makeRequest(`admin/invoiceImportStatus/${payload.id}`, 'get', payload, resolve, reject, options),
  getInvoiceImportHistory: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/invoiceImportHistory', 'get', payload, resolve, reject, options),
  invoiceStats: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/invoiceStats', 'get', payload, resolve, reject, options),
  getAdminTeam: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/getAdminTeam', 'get', payload, resolve, reject, options),
  getDeactivatedAdminTeam: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/getDeactivatedAdminTeam', 'get', payload, resolve, reject, options),
  deactivateAdminTeamMember: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/deactivateAdminTeamMember', 'put', payload, resolve, reject, options),
  reactivateAdminTeamMember: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/reactivateAdminTeamMember', 'put', payload, resolve, reject, options),
  // payload: { id_user, role }; reserved to role 500 by the api.
  updateAdminTeamMemberRole: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/updateAdminTeamMemberRole', 'put', payload, resolve, reject, options),
  postLogMissionClick: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/postLogMissionClick', 'post', payload, resolve, reject, options),
  getMissionActivityLogs: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/getMissionActivityLogs', 'get', payload, resolve, reject, options),
  getMissionContractFile: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/missionContractFile',
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postAmendmentRequestAction: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/amendmentRequestAction',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyActivityLogs: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/getCompanyActivityLogs',
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  postCompanyJustificatif: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/uploadCompanyJustificatif',
      'post',
      payload,
      resolve,
      reject,
      options
    ),
  getCompanyActivityJustificatif: (payload, resolve, reject, options = {}) =>
    makeRequest(
      'admin/getCompanyActivityJustificatif',
      'get',
      payload,
      resolve,
      reject,
      options
    ),
  getPennyLaneCategories: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/pennylane/categories', 'get', payload, resolve, reject, options),
  postPennyLaneCategory: (payload, resolve, reject, options = {}) =>
    makeRequest('admin/pennylane/categories', 'post', payload, resolve, reject, options),
  getCrmHome: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/home', payload, resolve, reject, options),
  getCrmTasks: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/tasks', payload, resolve, reject, options),
  countCrmOpenTasks: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/tasks/count', payload, resolve, reject, options),
  completeCrmClient: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/client', payload, resolve, reject, options),
  moveCrmCompanyToOld: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/old', payload, resolve, reject, options),
  moveCrmCompanyToNew: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/new', payload, resolve, reject, options),
  markCrmCompanyLost: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/lost', payload, resolve, reject, options),
  reactivateCrmProspect: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/prospect/reactivate', payload, resolve, reject, options),
  saveCrmCompanyContact: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/contact/save', payload, resolve, reject, options),
  deleteCrmCompanyContact: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/contact/delete', payload, resolve, reject, options),
  getCrmTask: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/task', payload, resolve, reject, options),
  createCrmTask: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/task/create', payload, resolve, reject, options),
  updateCrmTask: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/task/update', payload, resolve, reject, options),
  deleteCrmTask: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/task/delete', payload, resolve, reject, options),
  restoreCrmTask: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/task/restore', payload, resolve, reject, options),
  // A GET, unlike the other CRM routes; its url as abort key: a second read cancels the first.
  listCrmDeletedTasks: (payload, resolve, reject, options) =>
    makeRequest('admin/crm/tasks/deleted', 'get', {}, resolve, reject, options),
  setCrmTaskDone: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/task/done', payload, resolve, reject, options),
  assignCrmTask: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/task/assign', payload, resolve, reject, options),
  moveCrmTask: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/task/due', payload, resolve, reject, options),
  getCrmTaskActivity: (payload, resolve, reject, options) =>
    makeCrmRequest(
      'admin/crm/task/activity',
      payload,
      resolve,
      reject,
      options
    ),
  getCrmMentions: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/mentions', payload, resolve, reject, options),
  getCrmTaskComments: (payload, resolve, reject, options) =>
    makeCrmRequest(
      'admin/crm/task/comments',
      payload,
      resolve,
      reject,
      options
    ),
  addCrmTaskComment: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/task/comment', payload, resolve, reject, options),
  updateCrmTaskComment: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/task/comment/update', payload, resolve, reject, options),
  deleteCrmTaskComment: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/task/comment/delete', payload, resolve, reject, options),
  markCrmTaskRead: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/task/read', payload, resolve, reject, options),
  getCrmGroupInvoiceTotals: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/group/invoices/totals', payload, resolve, reject, options),
  getCrmTeam: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/team', payload, resolve, reject, options),
  getCrmContacts: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/contacts', payload, resolve, reject, options),
  setCrmPersonLead: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/person/lead', payload, resolve, reject, options),
  getCrmPersonLead: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/person/lead/get', payload, resolve, reject, options),
  deactivateCrmCompanyMember: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/member/deactivate', payload, resolve, reject, options),
  reactivateCrmCompanyMember: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/member/reactivate', payload, resolve, reject, options),
  getCrmContactRecord: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/contact/record', payload, resolve, reject, options),
  saveCrmContact: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/contact/save', payload, resolve, reject, options),
  getCrmPlanning: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/planning', payload, resolve, reject, options),
  placeCrmVisit: (payload, resolve, reject, options) =>
    makeCrmRequest(
      'admin/crm/planning/place',
      payload,
      resolve,
      reject,
      options
    ),
  placeCrmVisits: (payload, resolve, reject, options) =>
    makeCrmRequest(
      'admin/crm/planning/place-tour',
      payload,
      resolve,
      reject,
      options
    ),
  getCrmAppointment: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/appointment', payload, resolve, reject, options),
  createCrmAppointment: (payload, resolve, reject, options) =>
    makeCrmRequest(
      'admin/crm/appointment/create',
      payload,
      resolve,
      reject,
      options
    ),
  updateCrmAppointment: (payload, resolve, reject, options) =>
    makeCrmRequest(
      'admin/crm/appointment/update',
      payload,
      resolve,
      reject,
      options
    ),
  setCrmAppointmentDone: (payload, resolve, reject, options) =>
    makeCrmRequest(
      'admin/crm/appointment/status',
      payload,
      resolve,
      reject,
      options
    ),
  setCrmCallbackDone: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/callback/done', payload, resolve, reject, options),
  getCrmGroup: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/group', payload, resolve, reject, options),
  saveCrmGroupInfo: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/group/info/save', payload, resolve, reject, options),
  deleteCrmGroup: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/group/delete', payload, resolve, reject, options),
  saveCrmGroupContact: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/group/contact/save', payload, resolve, reject, options),
  deleteCrmGroupContact: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/group/contact/delete', payload, resolve, reject, options),
  makeCrmGroupFromCompany: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/group/from-company', payload, resolve, reject, options),
  removeCrmGroupCompany: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/group/company/remove', payload, resolve, reject, options),
  createCrmNote: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/note/create', payload, resolve, reject, options),
  updateCrmNote: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/note/update', payload, resolve, reject, options),
  deleteCrmNote: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/note/delete', payload, resolve, reject, options),
  getCrmNotes: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/notes', payload, resolve, reject, options),
  getCrmGroupActivity: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/group/activity', payload, resolve, reject, options),
  getCrmRecordCalls: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/record/calls', payload, resolve, reject, options),
  searchCrmTaskTempWorkers: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/tempworkers', payload, resolve, reject, options),
  deleteCrmAppointment: (payload, resolve, reject, options) =>
    makeCrmRequest(
      'admin/crm/appointment/delete',
      payload,
      resolve,
      reject,
      options
    ),
  listCrmAppointmentComments: (payload, resolve, reject, options) =>
    makeCrmRequest(
      'admin/crm/appointment/comments',
      payload,
      resolve,
      reject,
      options
    ),
  addCrmAppointmentComment: (payload, resolve, reject, options) =>
    makeCrmRequest(
      'admin/crm/appointment/comment',
      payload,
      resolve,
      reject,
      options
    ),
  updateCrmAppointmentComment: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/appointment/comment/update', payload, resolve, reject, options),
  deleteCrmAppointmentComment: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/appointment/comment/delete', payload, resolve, reject, options),
  respondCrmAppointment: (payload, resolve, reject, options) =>
    makeCrmRequest(
      'admin/crm/appointment/respond',
      payload,
      resolve,
      reject,
      options
    ),
  openCrmDaySession: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/session/day', payload, resolve, reject, options),
  startCrmCallSession: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/session/start', payload, resolve, reject, options),
  getCrmCallSession: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/session', payload, resolve, reject, options),
  endCrmCallSession: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/session/end', payload, resolve, reject, options),
  listCrmCallSessions: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/sessions', payload, resolve, reject, options),
  getCrmCallDay: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/calls/day', payload, resolve, reject, options),
  setCrmCallOrder: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/calls/order', payload, resolve, reject, options),
  getCrmGlobalList: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/calls/global', payload, resolve, reject, options),
  getCrmCallOverview: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/calls/overview', payload, resolve, reject, options),
  addCrmCallToDay: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/calls/add', payload, resolve, reject, options),
  removeCrmCallFromDay: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/calls/remove', payload, resolve, reject, options),
  restoreCrmCallToDay: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/calls/restore', payload, resolve, reject, options),
  getCrmCompanies: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/companies', payload, resolve, reject, options),
  assignCrmCompany: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/assign', payload, resolve, reject, options),
  getCrmCompanyCrm: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/crm', payload, resolve, reject, options),
  linkCrmCompanyContact: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/contact/link', payload, resolve, reject, options),
  setCrmCompanyEmail: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/email', payload, resolve, reject, options),
  getCrmCompanyActivity: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/activity', payload, resolve, reject, options),
  getCrmCompanyResults: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/results', payload, resolve, reject, options),
  setCrmCompanySize: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/size', payload, resolve, reject, options),
  setCrmCompanyPhone: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/phone', payload, resolve, reject, options),
  deleteCrmCompany: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/delete', payload, resolve, reject, options),
  getCrmArchivedCompanies: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/archived', payload, resolve, reject, options),
  restoreCrmCompany: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/company/restore', payload, resolve, reject, options),
  createCrmCall: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/call', payload, resolve, reject, options),
  getCrmClientSizeSettings: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/settings/client-sizes', payload, resolve, reject, options),
  saveCrmClientSizeSettings: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/settings/client-sizes/update', payload, resolve, reject, options),
  getCrmCallSettings: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/settings/calls', payload, resolve, reject, options),
  saveCrmCallSettings: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/settings/calls/update', payload, resolve, reject, options),
  getCrmStatsDays: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/stats/days', payload, resolve, reject, options),
  getCrmStatsReviews: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/stats/reviews', payload, resolve, reject, options),
  saveCrmStatsReview: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/stats/review', payload, resolve, reject, options),
  suggestCrmStatsChallenge: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/stats/review/suggest', payload, resolve, reject, options),
  getCrmStatsGoals: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/stats/goals', payload, resolve, reject, options),
  saveCrmStatsGoals: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/stats/goals/save', payload, resolve, reject, options),
  getCrmAgencyGoals: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/agency-goals', payload, resolve, reject, options),
  saveCrmAgencyGoals: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/agency-goals/save', payload, resolve, reject, options),
  getCrmAgencyGoalsProgress: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/agency-goals/progress', payload, resolve, reject, options),
  setCrmContactPosition: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/contact/position', payload, resolve, reject, options),
  sendCrmRecordEmail: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/record/email/send', payload, resolve, reject, options),
  getCrmRecordEmails: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/record/emails', payload, resolve, reject, options),
  cancelCrmRecordEmail: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/record/email/cancel', payload, resolve, reject, options),
  getCrmProspects: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/prospects', payload, resolve, reject, options),
  createCrmProspect: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/prospect/create', payload, resolve, reject, options),
  checkCrmProspectSirets: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/prospect/sirets', payload, resolve, reject, options),
  confirmCrmCall: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/call/confirm', payload, resolve, reject, options),
  getCrmCallHistory: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/calls/history', payload, resolve, reject, options),
  getCrmProspectSummary: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/prospect/summary', payload, resolve, reject, options),
  getCrmPhoneLive: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/phone/live', payload, resolve, reject, options),
  // Past the api's longest answer (three Wildix requests of 5 s), so that a
  // lost answer never keeps the next « Appel » locked.
  getCrmCallerLines: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/phone/caller-lines', payload, resolve, reject, options),
  dialCrmPhone: (payload, resolve, reject, options) =>
    makeCrmRequest('admin/crm/phone/dial', payload, resolve, reject, { timeoutMs: 20000, ...options }),
  connectSocketWithToken,
  disconnectSocket,
  getSocket: () => currentSocket,
  cache,
  cacheableEndpoints,
  generateCacheKey,
  patchSearchMissionCache,
  updateCacheEntry,
  invalidateMissionCache,
  invalidateCompanyMissionsCache,
};

export { dispatchSessionExpired };
export default ApiDatabase;
